/**
 * E013-S002: DatabaseDiscoveryBridge
 * E012-T015: Cross-Doc Relation Support
 * E012-T018: RBAC Database Visibility Filter
 *
 * Bridge module to provide workspace-wide database discovery data
 * to Lit components that don't have direct access to the DI container.
 *
 * This is a pragmatic solution that:
 * - Keeps upstream code (blocksuite) unchanged
 * - Provides typed access to workspace databases
 * - Can be replaced with a better solution later
 *
 * RBAC (E012-T018):
 * - accessibleDatabases$: RBAC-filtered list (for UI display/read)
 * - writableDatabases$: RBAC-filtered list (for relation targets that need writes)
 * - databases$: Unfiltered list (for internal lookups like findIncomingRelations)
 *
 * Cross-Doc Relations (E012-T015):
 * - createCrossDocInverseRelation(): Creates inverse relation in target doc with permission check
 * - Uses CrossDocRelationService for cross-doc operations
 *
 * Usage:
 * 1. In React app initialization: call setDatabaseDiscoverySource() and setCrossDocRelationService()
 * 2. In Lit components: call getWritableDatabases() for write targets,
 *    getAccessibleDatabases() for read/display UI, createCrossDocInverseRelation() for relations
 */

import type { LiveData } from '@toeverything/infra';

import type {
  RelationRowsWindow,
  RelationRowsWindowRequest,
} from './row-projection';

/**
 * Database info from workspace-wide discovery.
 * Mirrors DatabaseInfo from streamlain/services/database-discovery.ts
 */
export interface WorkspaceDatabaseInfo {
  /** Block ID of the database */
  blockId: string;
  /** Doc ID where the database lives */
  docId: string;
  /** Title of the doc (page name) */
  docTitle: string;
  /** Title of the database */
  databaseTitle: string;
  /** Whether the database is currently in trash. */
  isInTrash?: boolean;
  /** Canvas visibility flag for system-managed databases. */
  visible?: boolean;
  /** Columns of the database */
  columns: Array<{
    id: string;
    name: string;
    type: string;
  }>;
}

export type DatabaseDiscoveryUpdateDetail = {
  affectedDatabaseKeys: string[];
  affectedDatabaseIds: string[];
};

export function getDatabaseDiscoveryKey(
  database: Pick<WorkspaceDatabaseInfo, 'docId' | 'blockId'>
): string {
  return `${database.docId}:${database.blockId}`;
}

/**
 * Databases grouped by doc for UI display.
 */
export interface DatabasesByDoc {
  docId: string;
  docTitle: string;
  databases: WorkspaceDatabaseInfo[];
}

// Internal state - unfiltered (for internal lookups)
let databases$: LiveData<WorkspaceDatabaseInfo[]> | null = null;
let databasesByDoc$: LiveData<DatabasesByDoc[]> | null = null;
let databasesByBlockId = new Map<string, WorkspaceDatabaseInfo>();
let databasesByDiscoveryKey = new Map<string, WorkspaceDatabaseInfo>();
let databaseIndexSubscription: { unsubscribe: () => void } | null = null;
let databaseIndexUpdatedAt = 0;

const DATABASE_DISCOVERY_SETTLE_MS = 750;

// E012-T018: RBAC-filtered state (for UI)
let accessibleDatabases$: LiveData<WorkspaceDatabaseInfo[]> | null = null;
let accessibleDatabasesByDoc$: LiveData<DatabasesByDoc[]> | null = null;
let writableDatabases$: LiveData<WorkspaceDatabaseInfo[]> | null = null;
let writableDatabasesByDoc$: LiveData<DatabasesByDoc[]> | null = null;
let accessibleTrashcanDatabases$: LiveData<WorkspaceDatabaseInfo[]> | null =
  null;

/**
 * Sets the database discovery source.
 * Should be called once during app initialization from a React component
 * that has access to the DatabaseDiscoveryService.
 *
 * E012-T018: Now accepts both filtered and unfiltered lists.
 *
 * @param source$ - LiveData of all databases (unfiltered, for internal lookups)
 * @param byDocSource$ - LiveData of databases grouped by doc (unfiltered)
 * @param accessibleSource$ - LiveData of Doc_Read-filtered databases (for read/display UI)
 * @param accessibleByDocSource$ - LiveData of Doc_Read-filtered databases grouped by doc
 * @param writableSource$ - LiveData of Doc_Update-filtered databases (for write target UI)
 * @param writableByDocSource$ - LiveData of Doc_Update-filtered databases grouped by doc
 * @param accessibleTrashcanSource$ - LiveData of Doc_Read-filtered trashed databases
 */
export function setDatabaseDiscoverySource(
  source$: LiveData<WorkspaceDatabaseInfo[]>,
  byDocSource$: LiveData<DatabasesByDoc[]>,
  accessibleSource$?: LiveData<WorkspaceDatabaseInfo[]>,
  accessibleByDocSource$?: LiveData<DatabasesByDoc[]>,
  writableSource$?: LiveData<WorkspaceDatabaseInfo[]>,
  writableByDocSource$?: LiveData<DatabasesByDoc[]>,
  accessibleTrashcanSource$?: LiveData<WorkspaceDatabaseInfo[]>
): void {
  databaseIndexSubscription?.unsubscribe();
  databases$ = source$;
  databasesByDoc$ = byDocSource$;
  rebuildDatabaseIndex(source$.value);
  databaseIndexSubscription = source$.subscribe(rebuildDatabaseIndex);
  // E012-T018: Store accessible lists (fallback to unfiltered for backwards compat)
  accessibleDatabases$ = accessibleSource$ ?? source$;
  accessibleDatabasesByDoc$ = accessibleByDocSource$ ?? byDocSource$;
  writableDatabases$ = writableSource$ ?? accessibleDatabases$;
  writableDatabasesByDoc$ =
    writableByDocSource$ ?? accessibleDatabasesByDoc$ ?? byDocSource$;
  accessibleTrashcanDatabases$ = accessibleTrashcanSource$ ?? null;
}

export function clearDatabaseDiscoverySource(): void {
  databaseIndexSubscription?.unsubscribe();
  databaseIndexSubscription = null;
  databases$ = null;
  databasesByDoc$ = null;
  accessibleDatabases$ = null;
  accessibleDatabasesByDoc$ = null;
  writableDatabases$ = null;
  writableDatabasesByDoc$ = null;
  accessibleTrashcanDatabases$ = null;
  databaseIndexUpdatedAt = 0;
  rebuildDatabaseIndex([]);
}

function rebuildDatabaseIndex(source: WorkspaceDatabaseInfo[]): void {
  databaseIndexUpdatedAt = Date.now();
  databasesByBlockId = new Map(
    source.map(database => [database.blockId, database])
  );
  databasesByDiscoveryKey = new Map(
    source.map(database => [getDatabaseDiscoveryKey(database), database])
  );
}

/**
 * Gets all databases in the workspace.
 * Returns empty array if bridge not initialized.
 */
export function getWorkspaceDatabases(): WorkspaceDatabaseInfo[] {
  return databases$?.value ?? [];
}

/**
 * Gets databases grouped by doc.
 * Returns empty array if bridge not initialized.
 */
export function getDatabasesByDoc(): DatabasesByDoc[] {
  return databasesByDoc$?.value ?? [];
}

/**
 * Gets the LiveData for databases (for reactive subscriptions).
 * Returns null if bridge not initialized.
 */
export function getDatabasesLiveData(): LiveData<
  WorkspaceDatabaseInfo[]
> | null {
  return databases$;
}

/**
 * Gets the LiveData for databases by doc (for reactive subscriptions).
 * Returns null if bridge not initialized.
 */
export function getDatabasesByDocLiveData(): LiveData<DatabasesByDoc[]> | null {
  return databasesByDoc$;
}

/**
 * Checks if the bridge is initialized.
 */
export function isBridgeInitialized(): boolean {
  return databases$ !== null;
}

export function isDatabaseDiscoverySettling(): boolean {
  if (!databases$) return true;
  return Date.now() - databaseIndexUpdatedAt < DATABASE_DISCOVERY_SETTLE_MS;
}

export function getDatabaseDiscoverySettleDelayMs(): number {
  return DATABASE_DISCOVERY_SETTLE_MS;
}

/**
 * Finds a database by its block ID.
 */
export function findDatabaseByBlockId(
  blockId: string
): WorkspaceDatabaseInfo | undefined {
  return databasesByBlockId.get(blockId);
}

/**
 * Finds a database by its block ID and doc ID.
 * More precise lookup for cross-doc scenarios.
 */
export function findDatabaseByIds(
  docId: string,
  blockId: string
): WorkspaceDatabaseInfo | undefined {
  return databasesByDiscoveryKey.get(`${docId}:${blockId}`);
}

// =============================================================================
// E012-T018: RBAC-filtered getters for UI components
// =============================================================================

/**
 * E012-T018: Gets RBAC-filtered databases for UI display.
 * Only shows databases the user has Doc_Read permission for.
 * Use this for target database selection in UI.
 */
export function getAccessibleDatabases(): WorkspaceDatabaseInfo[] {
  return accessibleDatabases$?.value ?? [];
}

/**
 * Gets RBAC-filtered trashed databases. These are readable, but should not be
 * mixed into normal target pickers.
 */
export function getAccessibleTrashcanDatabases(): WorkspaceDatabaseInfo[] {
  return accessibleTrashcanDatabases$?.value ?? [];
}

/**
 * E012-T018: Gets RBAC-filtered databases grouped by doc.
 * Use this for grouped database pickers in UI.
 */
export function getAccessibleDatabasesByDoc(): DatabasesByDoc[] {
  return accessibleDatabasesByDoc$?.value ?? [];
}

/**
 * E012-T018: Gets the LiveData for accessible databases (for reactive subscriptions).
 */
export function getAccessibleDatabasesLiveData(): LiveData<
  WorkspaceDatabaseInfo[]
> | null {
  return accessibleDatabases$;
}

/**
 * E012-T018: Gets the LiveData for accessible databases by doc.
 */
export function getAccessibleDatabasesByDocLiveData(): LiveData<
  DatabasesByDoc[]
> | null {
  return accessibleDatabasesByDoc$;
}

/**
 * Gets Doc_Update-filtered databases for UI that will create or mutate
 * bidirectional relation schema. Read-only databases must remain displayable
 * through the accessible getters, but they are not valid write targets.
 */
export function getWritableDatabases(): WorkspaceDatabaseInfo[] {
  return writableDatabases$?.value ?? [];
}

/**
 * Gets Doc_Update-filtered databases grouped by doc.
 */
export function getWritableDatabasesByDoc(): DatabasesByDoc[] {
  return writableDatabasesByDoc$?.value ?? [];
}

/**
 * Gets the LiveData for writable databases.
 */
export function getWritableDatabasesLiveData(): LiveData<
  WorkspaceDatabaseInfo[]
> | null {
  return writableDatabases$;
}

/**
 * Gets the LiveData for writable databases by doc.
 */
export function getWritableDatabasesByDocLiveData(): LiveData<
  DatabasesByDoc[]
> | null {
  return writableDatabasesByDoc$;
}

// =============================================================================
// Relation delete interceptor suspension
// =============================================================================

let relationDeleteInterceptorSuspensionDepth = 0;

/**
 * Whether the relation delete interceptor must let `propertyDelete` through
 * without dialogs. Used while a confirmed flow (inverse cleanup, database
 * deletion) removes counterpart columns programmatically.
 */
export function isRelationDeleteInterceptorSuspended(): boolean {
  return relationDeleteInterceptorSuspensionDepth > 0;
}

/**
 * Run `fn` with the relation delete interceptor suspended. Nesting is safe;
 * the interceptor resumes once the outermost call settles.
 */
export async function runWithRelationDeleteInterceptorSuspended<T>(
  fn: () => Promise<T> | T
): Promise<T> {
  relationDeleteInterceptorSuspensionDepth += 1;
  try {
    return await fn();
  } finally {
    relationDeleteInterceptorSuspensionDepth -= 1;
  }
}

// =============================================================================
// E012-T015: Cross-Doc Relation Operations
// =============================================================================

/**
 * E012-T015: Result of a cross-doc relation operation
 */
export interface CrossDocRelationResult {
  success: boolean;
  inversePropertyId?: string;
  error?: Error;
}

/**
 * E012-T015: Interface for CrossDocRelationService
 * (Avoids importing the actual service to keep bridge lightweight)
 */
interface ICrossDocRelationService {
  createCrossDocInverseRelation(
    sourceDocId: string,
    sourceDatabaseId: string,
    targetDocId: string,
    targetDatabaseId: string,
    backlinkName: string,
    sourceRelationId: string,
    inverseCardinality: 'one' | 'many',
    sourceCardinality: 'one' | 'many'
  ): Promise<CrossDocRelationResult>;
  deleteCrossDocInverseRelation(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string
  ): Promise<CrossDocRelationResult>;
  canModifyDoc(docId: string): Promise<boolean>;
  updateCrossDocInverseCardinality(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string,
    newInverseCardinality: 'one' | 'many'
  ): Promise<CrossDocRelationResult>;
  updateCrossDocColumnCardinality(
    targetDocId: string,
    targetDatabaseId: string,
    inverseFieldId: string,
    newCardinality: 'one' | 'many'
  ): Promise<CrossDocRelationResult>;
  getCrossDocDatabaseRows(
    docId: string,
    databaseId: string,
    displayFieldId: string
  ): Promise<Array<{ id: string; display: string }>>;
  getCrossDocDatabaseRowsWindow(
    docId: string,
    databaseId: string,
    displayFieldId: string,
    request: RelationRowsWindowRequest
  ): Promise<RelationRowsWindow>;
  getCrossDocRowDisplayValue(
    docId: string,
    databaseId: string,
    displayFieldId: string,
    rowId: string
  ): Promise<string | undefined>;
  updateCrossDocCellValue(
    docId: string,
    databaseId: string,
    rowId: string,
    propertyId: string,
    value: string[]
  ): Promise<CrossDocRelationResult>;
  getCrossDocCellValue(
    docId: string,
    databaseId: string,
    rowId: string,
    propertyId: string
  ): Promise<{
    success: boolean;
    value?: string[];
    propertyType?: string;
    propertyData?: Record<string, unknown>;
    error?: Error;
  }>;
}

// E012-T015: Cross-doc relation service reference
let crossDocRelationService: ICrossDocRelationService | null = null;

/**
 * E012-T015: Sets the cross-doc relation service.
 * Should be called once during app initialization.
 */
export function setCrossDocRelationService(
  service: ICrossDocRelationService
): void {
  crossDocRelationService = service;
}

/**
 * E012-T015: Checks if cross-doc relation service is initialized.
 */
export function isCrossDocRelationServiceInitialized(): boolean {
  return crossDocRelationService !== null;
}

/**
 * E012-T015: Check if user can modify a document.
 * Returns false if service not initialized.
 */
export async function canModifyDoc(docId: string): Promise<boolean> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return false;
  }
  return crossDocRelationService.canModifyDoc(docId);
}

/**
 * E012-T015: Create an inverse relation in a target document.
 *
 * This is the main entry point for Lit components to create
 * cross-doc bidirectional relations with proper permission checks.
 *
 * @returns Result with success status and inversePropertyId or error
 */
export async function createCrossDocInverseRelation(
  sourceDocId: string,
  sourceDatabaseId: string,
  targetDocId: string,
  targetDatabaseId: string,
  backlinkName: string,
  sourceRelationId: string,
  inverseCardinality: 'one' | 'many' = 'many',
  sourceCardinality: 'one' | 'many' = 'many'
): Promise<CrossDocRelationResult> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.createCrossDocInverseRelation(
    sourceDocId,
    sourceDatabaseId,
    targetDocId,
    targetDatabaseId,
    backlinkName,
    sourceRelationId,
    inverseCardinality,
    sourceCardinality
  );
}

/**
 * E012-T016: Delete an inverse relation in a target document.
 * (Prepared for T016 implementation)
 */
export async function deleteCrossDocInverseRelation(
  targetDocId: string,
  targetDatabaseId: string,
  inverseFieldId: string
): Promise<CrossDocRelationResult> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.deleteCrossDocInverseRelation(
    targetDocId,
    targetDatabaseId,
    inverseFieldId
  );
}

/**
 * Update the inverseCardinality of an inverse relation in a target document.
 * Called when cardinality is changed to keep symmetric stealing in sync.
 */
export async function updateCrossDocInverseCardinality(
  targetDocId: string,
  targetDatabaseId: string,
  inverseFieldId: string,
  newInverseCardinality: 'one' | 'many'
): Promise<CrossDocRelationResult> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.updateCrossDocInverseCardinality(
    targetDocId,
    targetDatabaseId,
    inverseFieldId,
    newInverseCardinality
  );
}

/**
 * Update the cardinality of an inverse relation column in a target document.
 * Called when "Backlink Type" is changed on source to sync to inverse.cardinality.
 */
export async function updateCrossDocColumnCardinality(
  targetDocId: string,
  targetDatabaseId: string,
  inverseFieldId: string,
  newCardinality: 'one' | 'many'
): Promise<CrossDocRelationResult> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.updateCrossDocColumnCardinality(
    targetDocId,
    targetDatabaseId,
    inverseFieldId,
    newCardinality
  );
}

/**
 * Get rows from a cross-doc database for relation dropdown.
 * Uses CrossDocRelationService to open the remote doc.
 */
export async function getCrossDocDatabaseRows(
  docId: string,
  databaseId: string,
  displayFieldId: string
): Promise<Array<{ id: string; display: string }>> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return [];
  }

  return crossDocRelationService.getCrossDocDatabaseRows(
    docId,
    databaseId,
    displayFieldId
  );
}

export async function getCrossDocDatabaseRowsWindow(
  docId: string,
  databaseId: string,
  displayFieldId: string,
  request: RelationRowsWindowRequest
): Promise<RelationRowsWindow> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      offset: request.offset ?? 0,
      limit: request.limit,
      totalCount: 0,
      hasMore: false,
      rows: [],
    };
  }

  return crossDocRelationService.getCrossDocDatabaseRowsWindow(
    docId,
    databaseId,
    displayFieldId,
    request
  );
}

// =============================================================================
// Cross-Doc Row Display Value Cache
// =============================================================================

// Cache for cross-doc row display values: Map<`${docId}:${databaseId}:${rowId}`, displayValue>
const crossDocDisplayCache = new Map<string, string>();

/**
 * Get cached display value for a cross-doc row.
 * Returns undefined if not in cache.
 */
export function getCrossDocRowDisplayCached(
  docId: string,
  databaseId: string,
  rowId: string
): string | undefined {
  const key = `${docId}:${databaseId}:${rowId}`;
  return crossDocDisplayCache.get(key);
}

/**
 * Set cached display value for a cross-doc row.
 */
export function setCrossDocRowDisplayCached(
  docId: string,
  databaseId: string,
  rowId: string,
  displayValue: string
): void {
  const key = `${docId}:${databaseId}:${rowId}`;
  crossDocDisplayCache.set(key, displayValue);
}

/**
 * Get display value for a cross-doc row.
 * First checks cache, then loads async if needed.
 * Returns truncated rowId as fallback if not available.
 */
export async function getCrossDocRowDisplayValue(
  docId: string,
  databaseId: string,
  displayFieldId: string,
  rowId: string
): Promise<string> {
  // Check cache first
  const cached = getCrossDocRowDisplayCached(docId, databaseId, rowId);
  if (cached !== undefined) {
    return cached;
  }

  // Load from service
  if (!crossDocRelationService) {
    return rowId.slice(0, 8);
  }

  try {
    const displayValue =
      await crossDocRelationService.getCrossDocRowDisplayValue(
        docId,
        databaseId,
        displayFieldId,
        rowId
      );
    if (displayValue !== undefined) {
      setCrossDocRowDisplayCached(docId, databaseId, rowId, displayValue);
      return displayValue;
    }
    return rowId.slice(0, 8);
  } catch (e) {
    console.warn('[Bridge] Failed to get cross-doc row display:', e);
    return rowId.slice(0, 8);
  }
}

/**
 * Clear the cross-doc display cache.
 * Call this when navigating away or when data might be stale.
 */
export function clearCrossDocDisplayCache(): void {
  crossDocDisplayCache.clear();
}

/**
 * E018-S015-T001: Invalidate cache entries for a specific database.
 * Call this when a database is deleted to remove stale cached values.
 */
export function invalidateCacheForDatabase(
  docId: string,
  databaseId: string
): void {
  const prefix = `${docId}:${databaseId}:`;
  const keysToDelete: string[] = [];

  for (const key of crossDocDisplayCache.keys()) {
    if (key.startsWith(prefix)) {
      keysToDelete.push(key);
    }
  }

  for (const key of keysToDelete) {
    crossDocDisplayCache.delete(key);
  }

  if (keysToDelete.length > 0) {
    console.log(
      `[Bridge] Invalidated ${keysToDelete.length} cache entries for deleted database:`,
      databaseId
    );
  }
}

// =============================================================================
// Cross-Doc Cell Value Operations (for bidirectional row link sync)
// =============================================================================

/**
 * Update a cell value in a cross-doc database.
 * Used by sync.ts for bidirectional row link sync.
 */
export async function updateCrossDocCellValue(
  docId: string,
  databaseId: string,
  rowId: string,
  propertyId: string,
  value: string[]
): Promise<CrossDocRelationResult> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.updateCrossDocCellValue(
    docId,
    databaseId,
    rowId,
    propertyId,
    value
  );
}

/**
 * Get current cell value from a cross-doc database.
 * Used by sync.ts for reading inverse relation values.
 * E018-S008: Extended to return property metadata for Rollup formatting.
 */
export async function getCrossDocCellValue(
  docId: string,
  databaseId: string,
  rowId: string,
  propertyId: string
): Promise<{
  success: boolean;
  value?: string[];
  propertyType?: string;
  propertyData?: Record<string, unknown>;
  error?: Error;
}> {
  if (!crossDocRelationService) {
    console.warn('[Bridge] CrossDocRelationService not initialized');
    return {
      success: false,
      error: new Error('CrossDocRelationService not initialized'),
    };
  }

  return crossDocRelationService.getCrossDocCellValue(
    docId,
    databaseId,
    rowId,
    propertyId
  );
}

// =============================================================================
// E018-S008-T005: Unified Relation API Re-exports
// =============================================================================

export {
  canModifyTargetDoc,
  type CreateInverseRelationParams,
  createInverseRelationUnified,
  type DeleteInverseRelationParams,
  deleteInverseRelationUnified,
  type RelationOperationResult,
} from './unified-relation-service';
